import React from "react";
import "../pages/activity.css";

/**
 * Palette de couleurs équilibrée pour dark mode (juste milieu)
 */
const DARK_MODE_COLORS = [
  '#6B91B8', // Bleu doux
  '#9B84B0', // Violet doux
  '#7BA593', // Vert menthe sombre
  '#B8A080', // Beige chaud
  '#A893BA', // Mauve clair
  '#B89084', // Rose poudré
  '#80A5B8', // Bleu glacier
  '#A893A0', // Mauve rosé
  '#93B8A5', // Vert d'eau
  '#B8A593', // Sable doré
];

/**
 * Génère une couleur stable pour un opérateur (palette dark mode)
 * @param {number} operatorId - ID de l'opérateur
 * @returns {string} Couleur hexadécimale
 */
function colorForOperator(operatorId) {
  // Utiliser l'ID comme index dans la palette (avec wrap)
  return DARK_MODE_COLORS[operatorId % DARK_MODE_COLORS.length];
}

/**
 * Composant SVG pur pour afficher un histogramme groupé par semaine et opérateur
 * @param {{weeks: string[], operators: {id:number, name:string}[], series: Record<number, number[]>, maxY: number}} props
 */
export function WeeklyHoursChart({ weeks, operators, series, maxY }) {
  // État pour le tooltip avec position
  const [tooltip, setTooltip] = React.useState(null);
  const [mouseX, setMouseX] = React.useState(0);
  const [mouseY, setMouseY] = React.useState(0);
  
  // Dimensions du SVG (responsive)
  const dimensions = React.useMemo(() => {
    const screenWidth = window.innerWidth;
    const contentWidth = screenWidth > 1400 ? 920 : screenWidth > 1200 ? 800 : screenWidth > 900 ? 700 : 600;
    return {
      WIDTH: contentWidth,
      HEIGHT: 360,
      PADDING: { left: 48, right: 16, top: 24, bottom: 56 }
    };
  }, []);

  const { WIDTH, HEIGHT, PADDING } = dimensions;
  const innerWidth = WIDTH - PADDING.left - PADDING.right;
  const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;

  // Échelle Y: mapper 0..maxY vers innerHeight
  const scaleY = React.useCallback((value) => {
    if (maxY === 0) return innerHeight;
    return innerHeight * (1 - value / maxY);
  }, [maxY, innerHeight]);

  // Calculer les ticks Y "agréables"
  const yTicks = React.useMemo(() => {
    if (maxY === 0) return [0];
    const step = maxY <= 5 ? 1 : maxY <= 10 ? 2 : maxY <= 20 ? 4 : maxY <= 50 ? 10 : 20;
    const ticks = [];
    for (let i = 0; i <= maxY; i += step) {
      ticks.push(i);
    }
    if (!ticks.includes(maxY)) ticks.push(maxY);
    return ticks;
  }, [maxY]);

  // Calcul des positions des barres
  const barConfig = React.useMemo(() => {
    const groupWidth = innerWidth / (weeks?.length || 1);
    const barGap = 4;
    const groupPadding = 8;
    const availableWidth = groupWidth - 2 * groupPadding;
    const calculatedWidth = (availableWidth - ((operators?.length || 1) - 1) * barGap) / (operators?.length || 1);
    // Largeur max de 80px pour éviter des colonnes trop volumineuses
    const barWidth = Math.min(80, Math.max(8, calculatedWidth));
    return { groupWidth, barGap, groupPadding, barWidth };
  }, [innerWidth, weeks, operators]);

  // Mémoïser les positions pour performance
  const barPositions = React.useMemo(() => {
    if (!weeks || !operators || !series) return [];
    
    const positions = [];
    const { groupWidth, barGap, groupPadding, barWidth } = barConfig;
    const MIN_BAR_HEIGHT = 2; // Hauteur minimale pour les barres à 0h
    
    weeks.forEach((week, weekIndex) => {
      const groupX = PADDING.left + weekIndex * groupWidth;
      
      operators.forEach((operator, opIndex) => {
        const hours = series[operator.id]?.[weekIndex] || 0;
        const barX = groupX + groupPadding + opIndex * (barWidth + barGap);
        
        // Calcul hauteur avec minimum pour valeurs à 0
        let barHeight, barY;
        if (hours > 0) {
          barHeight = innerHeight - scaleY(hours);
          barY = PADDING.top + scaleY(hours);
        } else {
          // Barre minimale visible pour 0h
          barHeight = MIN_BAR_HEIGHT;
          barY = PADDING.top + innerHeight - MIN_BAR_HEIGHT;
        }
        
        positions.push({
          week,
          operator,
          hours,
          x: barX,
          y: barY,
          width: barWidth,
          height: barHeight,
          color: colorForOperator(operator.id),
        });
      });
    });
    return positions;
  }, [weeks, operators, series, barConfig, PADDING, innerHeight, scaleY]);

  // Position X pour le label de semaine (centré sur le groupe)
  const weekLabelX = React.useCallback((weekIndex) => {
    return PADDING.left + weekIndex * barConfig.groupWidth + barConfig.groupWidth / 2;
  }, [PADDING, barConfig]);

  // Cas vides - retour conditionnel APRÈS tous les hooks
  if (!weeks || weeks.length === 0 || !operators || operators.length === 0) {
    return null;
  }

  return (
    <div className="chart-wrap" style={{ position: 'relative' }}>
      {/* Tooltip flottant */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          top: `${mouseY - 100}px`,
          left: `${mouseX}px`,
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #2d3142 0%, #1a1d2e 100%)',
          border: '1px solid rgba(95, 168, 255, 0.3)',
          borderRadius: '12px',
          padding: '12px 16px',
          color: '#fff',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          zIndex: 1000,
          pointerEvents: 'none',
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{ marginBottom: '4px', color: '#cbd3ea', fontSize: '12px' }}>
            Semaine du {tooltip.week}
          </div>
          <div style={{ color: '#5fa8ff', fontSize: '16px', fontWeight: '700' }}>
            {tooltip.name}
          </div>
          <div style={{ marginTop: '6px', fontSize: '18px', fontWeight: '800', color: '#e6ecff' }}>
            {tooltip.hours}h {tooltip.minutes > 0 && `${tooltip.minutes}min`}
          </div>
        </div>
      )}
      
      <svg 
        width={WIDTH} 
        height={HEIGHT} 
        className="wh-svg"
        role="img"
        aria-label={`Histogramme des heures travaillées par semaine du ${weeks[0]} au ${weeks[weeks.length - 1]}`}
      >
        <title>Heures travaillées par semaine et par opérateur</title>
        <desc>
          Histogramme groupé montrant les heures travaillées pour chaque opérateur sur {weeks.length} semaines,
          de {weeks[0]} à {weeks[weeks.length - 1]}.
        </desc>

        {/* Grille horizontale (lignes Y) */}
        {yTicks.map((tick) => {
          const y = PADDING.top + scaleY(tick);
          return (
            <g key={tick}>
              <line
                x1={PADDING.left}
                y1={y}
                x2={PADDING.left + innerWidth}
                y2={y}
                className="grid"
              />
              <text
                x={PADDING.left - 10}
                y={y}
                className="y-label"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* Barres */}
        {barPositions.map((bar, index) => {
          const totalMinutes = Math.round(bar.hours * 60);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          const isHovered = tooltip?.week === bar.week && tooltip?.opId === bar.operator.id;
          
          return (
            <g key={`${bar.week}-${bar.operator.id}`}>
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={bar.color}
                rx="4"
                style={{
                  opacity: isHovered ? 0.8 : 1,
                  transition: 'opacity 0.2s ease, transform 0.2s ease',
                  cursor: 'pointer',
                  filter: isHovered ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none'
                }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setMouseX(rect.left + rect.width / 2);
                  setMouseY(rect.top);
                  setTooltip({ week: bar.week, opId: bar.operator.id, name: bar.operator.name, hours, minutes });
                }}
                onMouseLeave={() => setTooltip(null)}
              />
              
              {/* Valeur au-dessus de la barre si assez haute et > 0 */}
              {bar.height > 20 && bar.hours > 0.01 && (
                <text
                  x={bar.x + bar.width / 2}
                  y={bar.y - 6}
                  className="bar-value"
                >
                  {bar.hours.toFixed(1)}
                </text>
              )}
            </g>
          );
        })}

        {/* Labels des semaines (axe X) */}
        {weeks.map((week, index) => (
          <text
            key={week}
            x={weekLabelX(index)}
            y={HEIGHT - 20}
            className="x-label"
          >
            {week}
          </text>
        ))}
      </svg>

      {/* Légende */}
      <div className="legend">
        {operators.map((operator) => (
          <div className="legend-item" key={operator.id}>
            <span 
              className="legend-swatch" 
              style={{ backgroundColor: colorForOperator(operator.id) }} 
            />
            <span className="legend-text">{operator.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}